import React from 'react';
import { View, StyleSheet, Modal, Platform, Pressable, ScrollView, useWindowDimensions } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography, Button } from './ui';
import { useTheme } from '../context/ThemeContext';
import { fonts } from '../styles/typography';

import { surfaceFill } from '../styles/platformBackgrounds';
interface BrandedConfirmModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive action — colours the primary button red and swaps the icon. */
  destructive?: boolean;
  /** Override icon name. Defaults to alert-circle-outline (or warning when destructive). */
  iconName?: keyof typeof Ionicons.glyphMap;
  confirmTestID?: string;
  cancelTestID?: string;
  /**
   * CORE-1117 — запрос в полёте: подтверждение остаётся на экране, но нажать
   * его второй раз нельзя. Без этого «вызвать endpoint ровно один раз»
   * держалось бы только на скорости пальца.
   */
  busy?: boolean;
  /** Одно действие вместо двух — для сообщений, где отменять нечего. */
  hideCancel?: boolean;
}

export function BrandedConfirmModal({
  visible,
  onCancel,
  onConfirm,
  title,
  message,
  confirmLabel = 'Подтвердить',
  cancelLabel = 'Отмена',
  destructive = false,
  iconName,
  confirmTestID = 'confirm-modal-confirm',
  cancelTestID = 'confirm-modal-cancel',
  busy = false,
  hideCancel = false,
}: BrandedConfirmModalProps) {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const { colors: themeColors, isDark } = useTheme();

  if (!visible) return null;

  const resolvedIcon: keyof typeof Ionicons.glyphMap =
    iconName ?? (destructive ? 'warning-outline' : 'alert-circle-outline');
  const accent = destructive ? '#EF4444' : '#E98245';

  const layer = (
    // Пока запрос в полёте, клик по фону не закрывает: закрытое окно читается
    // как «отменил», а удаление уже отправлено.
    <Pressable style={styles.backdrop} onPress={busy ? undefined : onCancel}>
      {/* CORE-1117 — прокрутка между фоном и карточкой.
          QA на 390×844: верх окна с заголовком оказывался выше видимой области.
          Причина не в отступах: карточка центрировалась флексом, и когда она
          выше экрана, лишнее уходит за ОБА края, а до верхнего края добраться
          нечем — прокручивать было нечего. Короткая карточка по-прежнему
          ровно по центру (`flexGrow` + `center`), высокая становится
          прокручиваемой с самого верха. */}
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        // Нажатие по кнопке внутри не должно требовать второго тапа после
        // прокрутки.
        keyboardShouldPersistTaps="handled"
      >
      {/* Закрытие по клику мимо карточки повешено ЗДЕСЬ, а не только на фон:
          прокрутка легла между ними, и на нативе она забирает жест себе —
          нажатие по пустому месту до фона бы не дошло. */}
      <Pressable style={styles.scrollFill} onPress={busy ? undefined : onCancel}>
      <Pressable
        style={[
          styles.card,
          isDesktop && styles.cardDesktop,
          // CORE-1733 (возврат QA) — окно подтверждения — поверхность v4.
          surfaceFill(isDark ? 'dark' : 'light'),
        ]}
        onPress={(e) => e.stopPropagation()}
      >
        <View style={[styles.iconCircle, { backgroundColor: destructive ? 'rgba(239,68,68,0.10)' : 'rgba(233, 130, 69,0.08)' }]}>
          <Ionicons name={resolvedIcon} size={26} color={accent} />
        </View>

        <Typography variant="h3" style={[styles.heading, { color: themeColors.textPrimary }]}>
          {title}
        </Typography>
        <Typography variant="body" style={[styles.body, { color: themeColors.textPrimary }]}>
          {message}
        </Typography>

        <View style={styles.buttons}>
          <Button
            testID={confirmTestID}
            variant={destructive ? 'secondary' : 'convex-fire'}
            onPress={onConfirm}
            isLoading={busy}
            disabled={busy}
            style={[
              styles.btn,
              destructive && {
                backgroundColor: accent,
                borderColor: accent,
              },
              isDark && destructive && { borderColor: accent },
            ]}
          >
            {confirmLabel}
          </Button>
          {!hideCancel && (
            <Button
              testID={cancelTestID}
              variant="secondary"
              onPress={onCancel}
              disabled={busy}
              style={[styles.btn, isDark && { borderColor: 'rgba(255,255,255,0.15)' }]}
            >
              {cancelLabel}
            </Button>
          )}
        </View>
      </Pressable>
      </Pressable>
      </ScrollView>
    </Pressable>
  );

  // CORE-1117 (QA FAIL 31.08 и повторный 03.09) — на вебе окно уезжает В ПОРТАЛ,
  // а не остаётся там, где его отрендерили.
  //
  // Симптом оба раза был один: карточка встаёт «из фактической позиции
  // раздела», верх с заголовком уходит под шапку. Чинили это дважды изнутри
  // поддерева — слоем (`zIndex`) и прокруткой, — и оба раза не удержало.
  //
  // Потому что причина не в слое. `position: fixed` отсчитывается от окна
  // браузера ровно до тех пор, пока ни у одного предка нет `transform`,
  // `filter`, `perspective`, `contain` или `backdrop-filter`: у такого предка
  // `fixed` начинает считаться ОТ НЕГО. Подтверждение живёт глубоко внутри
  // прокручиваемой вкладки настроек, и «сверху» для него — верх раздела, а не
  // верх экрана. Поднимать `zIndex` в этом положении бесполезно: элемент лежит
  // не в том месте, а не под чужим слоем.
  //
  // `Modal` из react-native-web рисует содержимое через `createPortal` в
  // `document.body` (`ModalPortal`), то есть снаружи всего приложения. Там ни
  // одного предка с трансформацией нет по построению, и `fixed` снова значит
  // «от окна». Собственный слой портала — `zIndex: 9999`, столько же, сколько у
  // шапки, но в DOM он стоит ПОСЛЕ приложения и потому рисуется выше; наш
  // внутренний `zIndex` остаётся запасом.
  //
  // Только веб. На нативе `Modal` уже используется вызывающими (`NativeOverlay`
  // на прокручиваемых вкладках), и второй такой здесь дал бы вложенные модалки.
  if (Platform.OS !== 'web') return layer;
  return (
    <Modal visible transparent animationType="none" onRequestClose={busy ? undefined : onCancel}>
      {layer}
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    // CORE-1117 — выше шапки и её выпадающих меню.
    //
    // Шапка `position: fixed` с `zIndex: 9999`, её меню — 10000 и 10001. При
    // прежних 6000 подтверждение рисовалось ПОД ними: на мобильном, где шапка
    // занимает верх экрана, заголовок окна ею и закрывался. Подтверждение —
    // самый верхний слой по смыслу: пока на него отвечают, остальным интерфейсом
    // не пользуются.
    zIndex: 10100,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(4px)',
    }),
  } as any,
  // CORE-1117 — у прокрутки должна быть СВОЯ высота, иначе прокручивать нечего.
  //
  // Без `flex: 1` высота бралась по содержимому, фон центрировал получившийся
  // блок, и карточка выше экрана вылезала за оба края — а до верхнего было не
  // добраться: прокрутки не возникало вовсе. Ровно это и видела QA на
  // 1280×720, где карточка удаления (два абзаца последствий) перерастает
  // видимую область на полсотни точек.
  scroll: { flex: 1, width: '100%' },
  // `flexGrow` вместо `flex`: короткая карточка остаётся по центру, высокая
  // растягивает содержимое и прокручивается, а не обрезается.
  scrollContent: { flexGrow: 1 },
  scrollFill: {
    flexGrow: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    // Воздух сверху и снизу: у высокой карточки видно, что она прокручивается.
    paddingVertical: 24,
  },
  card: {
    borderRadius: 32,
    paddingHorizontal: 32,
    paddingTop: 40,
    paddingBottom: 32,
    width: '90%',
    maxWidth: 420,
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.05)',
    }),
  } as any,
  cardDesktop: {
    paddingHorizontal: 48,
    paddingTop: 48,
    paddingBottom: 40,
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  heading: {
    textAlign: 'center',
    marginBottom: 8,
    fontFamily: fonts.heading,
    fontWeight: 'normal',
    fontSize: 26,
    letterSpacing: -0.5,
  },
  body: {
    textAlign: 'center',
    opacity: 0.7,
    marginBottom: 32,
    lineHeight: 24,
    fontSize: 16,
    maxWidth: 360,
    fontFamily: fonts.text,
    fontWeight: 'normal',
  },
  buttons: {
    width: '100%',
    gap: 12,
  },
  btn: {
    width: '100%',
    height: 52,
    borderRadius: 14,
    // iOS: у Button (md) paddingVertical 16 — из 52 на подпись оставалось 18 при
    // lineHeight 24, и iOS рисовал её ниже центра с обрезанным низом. Высоту
    // держит сама кнопка; Android и веб центрируют строку сами — их не трогаем.
    ...(Platform.OS === 'ios' && { paddingVertical: 0 }),
  },
});
